import { Box, Paper, useMediaQuery } from '@mui/material';
import { SidebarNavItemCollapseProvider, SidebarProvider } from './Sidebar/context';

import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import GlobalFAB from '../GlobalFAB';
import { GlobalFABProvider } from '../GlobalFAB/GlobalFABContext';
import { useQueryState } from 'nuqs';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { BACKGROUND_COLORS, JOB_SITE_COLORS } from '@/common/presentation/constants/colors';
import { isFullWidthRoute } from '@/common/presentation/constants/fullWidthRoutes';
import { useLayoutStore } from '@/store/LayoutStore';
import { trackPageView } from '@/common/infrastructure/config/fullstoryInit';
import { useFeatureFlag } from '@/common/presentation/hooks/useFeatureFlag';
import { useEmbeddedMode } from '@/common/presentation/hooks/useEmbeddedMode';
import { FEATURE_FLAGS } from '@/common/domain/constants/featureFlags';
import { useUserPreferences } from '@/common/application/hooks/userPreferencesHook';
import { PREFERENCE_KEYS } from '@/store/UserPreferencesStore';
import '@/assets/scss/style.scss';

const isUserManagementPath = (pathname: string) => pathname.startsWith('/user-management');

const Layout = () => {
  const location = useLocation();
  const isEmbedded = useEmbeddedMode();
  const { preferences, setPreference } = useUserPreferences();
  const [moduleSearchParam] = useQueryState('module');
  const umFabKey = PREFERENCE_KEYS.USER_MANAGEMENT_GLOBAL_FAB_VISIBLE;
  const isOnUserManagement = useMemo(() => isUserManagementPath(location.pathname), [location.pathname]);
  const userManagementWantsFab = (preferences[umFabKey] as boolean | undefined) === true;
  const [isVisible, setIsVisible] = useState(isUserManagementPath(location.pathname) ? userManagementWantsFab : true);
  const isUserManagementRef = useRef(isOnUserManagement);
  isUserManagementRef.current = isOnUserManagement;

  const setGlobalFabVisible = useCallback(
    (visible: boolean) => {
      setIsVisible(visible);
      if (isUserManagementRef.current) {
        setPreference(umFabKey, visible);
      }
    },
    [setPreference, umFabKey]
  );
  const [subModuleSearchParam] = useQueryState('submodule');
  const [tabSearchParam] = useQueryState('tab');
  const hasPadding = useLayoutStore((state) => state.hasPadding);
  const isJobsErpDetailsPageEnabled = useFeatureFlag(FEATURE_FLAGS.JOBS_ERP_DETAILS_PAGE_NEW_LOOK_AND_FEEL);
  const isFullWidthHeader = isFullWidthRoute({
    module: moduleSearchParam,
    submodule: subModuleSearchParam,
    tab: tabSearchParam,
    pathname: location.pathname,
    featureFlags: {
      jobsErpDetailsPageEnabled: isJobsErpDetailsPageEnabled
    }
  });
  const isMobile = useMediaQuery('(max-width: 767px)');
  const shouldHavePadding = hasPadding && !isFullWidthHeader && !isEmbedded;

  // Track FullStory page views when route or query params change
  useEffect(() => {
    trackPageView(location.pathname, {
      module: moduleSearchParam,
      submodule: subModuleSearchParam
    });
  }, [location.pathname, moduleSearchParam, subModuleSearchParam]);

  // User Management: FAB off by default (preference); other routes: FAB shown unless the global flag turns it off.
  // TROC-317: never show FAB in ERP embed chrome-hide mode.
  useEffect(() => {
    if (isEmbedded) {
      setIsVisible(false);
      return;
    }
    if (isOnUserManagement) {
      setIsVisible(userManagementWantsFab);
    } else {
      setIsVisible(true);
    }
  }, [isOnUserManagement, userManagementWantsFab, isEmbedded]);

  return (
    <GlobalFABProvider isVisible={isVisible} setIsVisible={setGlobalFabVisible}>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '100vh',
          backgroundColor: isFullWidthHeader ? JOB_SITE_COLORS.background : BACKGROUND_COLORS.WHITE,
          overflow: 'hidden'
        }}
      >
        {!isEmbedded && (
          <SidebarProvider>
            <SidebarNavItemCollapseProvider>
              <Header />
              <Sidebar />
            </SidebarNavItemCollapseProvider>
          </SidebarProvider>
        )}

        <Paper
          sx={{
            flexGrow: 1,
            flexBasis: 0,
            minHeight: 0,
            p: shouldHavePadding ? 4 : 0,
            ...(isOnUserManagement || isEmbedded
              ? {
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  height: '100%'
                }
              : {
                  minHeight: '100vh',
                  overflow: 'auto'
                }),
            backgroundColor: isFullWidthHeader ? JOB_SITE_COLORS.background : BACKGROUND_COLORS.WHITE,
            // Skip mobile header offset when chrome is hidden (TROC-317 embed).
            pt: isMobile && !isEmbedded ? '64px' : undefined
          }}
        >
          <Outlet />
        </Paper>

        {!isEmbedded && <GlobalFAB />}
      </Box>
    </GlobalFABProvider>
  );
};

export default Layout;
